import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ImageOff } from 'lucide-react';
import type { OrderListRow } from '@mlm/shared';
import { fmtPeso, productImageUrl, store } from '../lib/api';
import { qk } from '../lib/queries';
import { CoachButton, Eyebrow, ErrorNote, Loading, Screen } from '../components/primitives';
import { ShopNav } from '../components/shop-nav';

/**
 * A member's orders. The status word is the SERVER's `status` put into plain
 * English — never recomputed here, because the ladder a given order walks
 * depends on its channel and its payment method, which this screen cannot see.
 */
const STATUS_COPY: Record<string, string> = {
  to_pay: 'Waiting for payment',
  payment_review: 'Checking your payment',
  paid: 'Paid',
  to_ship: 'Preparing to ship',
  shipping: 'On the way',
  delivered: 'Delivered',
  to_pick: 'Ready for pickup',
  to_release: 'Ready for release',
  completed: 'Completed',
  cancelled: 'Cancelled',
  refunded: 'Refunded',
};

export function ShopOrdersPage() {
  const q = useQuery({
    queryKey: qk.myOrders,
    queryFn: () => store.myOrders({ limit: 50 }),
    staleTime: 30_000,
  });

  return (
    <Screen eyebrow="Shop" title="Your orders" wide>
      <ShopNav />
      {q.isPending ? (
        <Loading label="Loading your orders" />
      ) : q.error ? (
        <ErrorNote error={q.error} retry={() => void q.refetch()} />
      ) : (q.data?.length ?? 0) === 0 ? (
        <div className="px-5 py-14 text-center">
          <Eyebrow>Nothing yet</Eyebrow>
          <p className="mt-2 text-[0.93rem] text-ink/70">You have not placed an order.</p>
          <Link to="/shop" className="mt-6 inline-block">
            <CoachButton>Browse the store</CoachButton>
          </Link>
        </div>
      ) : (
        <ul>
          {q.data!.map((o) => (
            <OrderCard key={o.id} order={o} />
          ))}
        </ul>
      )}
    </Screen>
  );
}

function OrderCard({ order }: { order: OrderListRow }) {
  const first = order.items[0];
  const more = order.items.length - 1;
  return (
    <li className="border-b border-border">
      <Link
        to={`/orders/${order.id}`}
        className="flex items-start gap-4 px-5 py-5 transition-colors hover:bg-surface focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-verde"
      >
        <div className="h-16 w-16 shrink-0 overflow-hidden border border-border bg-surface">
          {first?.productImages?.length ? (
            <img
              src={productImageUrl(first.productId, 0)}
              alt=""
              loading="lazy"
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-sage-deep">
              <ImageOff className="h-5 w-5" aria-hidden />
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <Eyebrow>{order.code}</Eyebrow>
          <p className="mt-1 line-clamp-2 text-[0.93rem] leading-snug text-ink">
            {first?.productName ?? 'Order'}
            {more > 0 && <span className="text-ink/60"> and {more} more</span>}
          </p>
          <p className="mt-1 text-[0.85rem] text-sage-deep">
            {STATUS_COPY[order.status] ?? order.status.replace(/_/g, ' ')}
          </p>
        </div>
        <p className="shrink-0 font-display text-[1.05rem] font-semibold text-ink">
          {fmtPeso(order.total)}
        </p>
      </Link>
    </li>
  );
}
